import { PictureAsPdf } from "@mui/icons-material";
import { Box, Button, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import BgBox from "../../../components/BgBox";
import CtrlOutTxtField from "../../../components/CtrlOutTxtField";
import CustomDropDown from "../../../components/CustomDropDown";
import LoadingScreen from "../../../components/loadingScreen";
import StyledTableContainer from "../../../components/StyledTableContainer";
import { customAlert } from "../../../components/notify";
import QueryKey from "../../../QueryKey";
import { getAllTrkOpWPgSch, getTrkOpLedger } from "../../../services/trkOp-service";
import { trkOpLedgerPdf } from "../../../services/pdf-service";
import { useAuthContext } from "../../../context/AuthContext";
import { capFirstLetter, formatPymtType, getLast30Days } from "../../../utils/format";

const VOUCHER_LABELS = {
    invoice: "Invoice",
    payment: "Payment",
    aHalting: "Halting",
    aUlCooliDrv: "UL Coolie Paid by Driver",
    aLdCooliDrv: "Loading Coolie Paid by Driver",
    aIncidental: "Incidental Charges",
    aOthers: "Others (+)",
    dTds: "TDS",
    dCommission: "Commission",
    dPenalty: "Penalty",
    dClaim: "Claim",
    dLdCooli: "Loading Coolie",
    dUlCooli: "Unloading Coolie",
    dFrtLess: "Recd Freight Less",
    dOthers: "Others (-)",
};

function fmtAmt(n) {
    if (!n) return "";
    return Number(n).toLocaleString('en-IN');
}

function fmtBal(n) {
    return Number(n || 0).toLocaleString('en-IN');
}

function fmtDate(d) {
    return new Date(d).toLocaleDateString('en-GB');
}

// Period of transactions must be within 6 months.
function isRangeOverSixMonths(fromDate, toDate) {
    const from = new Date(fromDate);
    const limit = new Date(from);
    limit.setMonth(limit.getMonth() + 6);
    return new Date(toDate) > limit;
}

function TrkOpLedgerPage() {
    const { t } = useTranslation();
    const { setLoadingScreen } = useAuthContext();
    const { today, priorDate } = getLast30Days();

    const [truckopId, setTruckopId] = useState("");
    const [fromDate, setFromDate] = useState(priorDate);
    const [toDate, setToDate] = useState(today);

    const rangeInvalid = !fromDate || !toDate || new Date(fromDate) > new Date(toDate);
    const rangeTooLong = !rangeInvalid && isRangeOverSixMonths(fromDate, toDate);
    const canFetch = Boolean(truckopId) && !rangeInvalid && !rangeTooLong;

    const { data: trkOpRows } = useQuery(
        [QueryKey.trkOpList, "ledgerDD"],
        () => getAllTrkOpWPgSch({ limit: 1000, pageNumber: 1, searchKeyWord: "" }));

    const trkOpDDArr = (trkOpRows?.data || []).map((op) => ({
        value: String(op.truckopId),
        label: `${capFirstLetter(op.fName)} ${capFirstLetter(op.lName)}`,
    }));

    const { isLoading, isError, error, data: ledger } = useQuery(
        [QueryKey.trkOpLedger, truckopId, fromDate, toDate],
        () => getTrkOpLedger({ truckopId, fromDate, toDate }),
        { enabled: canFetch, keepPreviousData: true });

    if (isError) {
        customAlert(error);
        return <h2>Something went wrong</h2>
    }

    let running = ledger?.broughtForward || 0;
    const rowsWithBal = (ledger?.rows || []).map((r) => {
        running = running + (r.debit || 0) - (r.credit || 0);
        return { ...r, balance: running };
    });
    const totalDebit = rowsWithBal.reduce((s, r) => s + (r.debit || 0), 0);
    const totalCredit = rowsWithBal.reduce((s, r) => s + (r.credit || 0), 0);

    async function handleDownloadPdf() {
        setLoadingScreen(true);
        try {
            const partyName = trkOpDDArr.find((op) => op.value === String(truckopId))?.label || "";
            const res = await trkOpLedgerPdf({ truckopId, fromDate, toDate, partyName });
            window.open(res.fileUrl, "_blank");
        } catch (err) {
            customAlert(err);
        }
        setLoadingScreen(false);
    }

    const headCellSx = { fontWeight: "bold", backgroundColor: "#F6F5FB" };
    const amtCellSx = { textAlign: "right", whiteSpace: "nowrap" };

    return (
        <BgBox px={4} py={1}>
            <Stack direction={'row'} justifyContent="space-between" alignItems={"flex-end"} mb={1.5} mt={0.5} px={2}>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>{t("bo.trkOpLedgerPg.title")}</Typography>

                <Stack direction={'row'} alignItems={"flex-end"} gap={2}>
                    <CustomDropDown title={t("bo.trkOpLedgerPg.selOp")} height={36} width={220} fontSize={13}
                        handleDDChange={(e) => setTruckopId(e.target.value)} ddArr={trkOpDDArr} defaultValue={truckopId} />
                    <CtrlOutTxtField value={fromDate} name={"fromDate"} onChange={(e) => setFromDate(e.target.value)} title={t("bo.filterMdl.fromDate")} fontSize={13} height={35} width={150} type={"date"} />
                    <CtrlOutTxtField value={toDate} name={"toDate"} onChange={(e) => setToDate(e.target.value)} title={t("bo.filterMdl.toDate")} fontSize={13} height={35} width={150} type={"date"} />
                    <Button variant='contained' sx={{ height: 36 }} startIcon={<PictureAsPdf />} color='primary' disabled={!canFetch} onClick={handleDownloadPdf}>{t("bo.reptListPg.pdf")}</Button>
                </Stack>
            </Stack>

            {rangeTooLong &&
                <Typography sx={{ color: "error.main", fontSize: 13, px: 2, mb: 1 }}>{t("bo.trkOpLedgerPg.sixMonthErr")}</Typography>}

            {!truckopId ?
                <Stack alignItems="center" justifyContent="center" height="70%">
                    <Typography sx={{ color: "text.secondary" }}>{t("bo.trkOpLedgerPg.pickOpMsg")}</Typography>
                </Stack>
                : (isLoading && canFetch) ?
                    <Box sx={{ position: "absolute", top: 0, left: 0 }}><LoadingScreen /></Box>
                    :
                    <StyledTableContainer height={"85%"}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>{t("bo.trkOpLedgerPg.date")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.trkOpLedgerPg.voucher")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.trkOpLedgerPg.pymtType")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.trkOpLedgerPg.trkNo")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.trkOpLedgerPg.remarks")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.trkOpLedgerPg.debit")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.trkOpLedgerPg.credit")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.trkOpLedgerPg.balance")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ backgroundColor: "#FBFAF4" }}>
                                <TableCell colSpan={7} sx={{ fontWeight: "600" }}>{t("bo.trkOpLedgerPg.broughtFwd")}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "600" }}>{fmtBal(ledger?.broughtForward)}</TableCell>
                            </TableRow>
                            {rowsWithBal.map((r, idx) => (
                                <TableRow key={idx} hover>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.txnDate)}</TableCell>
                                    <TableCell>{VOUCHER_LABELS[r.voucher] || r.voucher}{r.voucherFreeTxt ? ` (${r.voucherFreeTxt})` : ""}</TableCell>
                                    <TableCell>{r.paymentType ? formatPymtType(r.paymentType) : ""}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.trkRegNo?.toUpperCase() || ""}</TableCell>
                                    <TableCell>{r.truckOpReqId}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtAmt(r.debit)}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtAmt(r.credit)}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtBal(r.balance)}</TableCell>
                                </TableRow>
                            ))}
                            {rowsWithBal.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: "center", color: "text.secondary" }}>{t("bo.trkOpLedgerPg.noTxns")}</TableCell>
                                </TableRow>}
                            <TableRow sx={{ backgroundColor: "#F6F5FB" }}>
                                <TableCell colSpan={5} sx={{ fontWeight: "bold" }}>{t("bo.trkOpLedgerPg.total")}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtAmt(totalDebit)}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtAmt(totalCredit)}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtBal((ledger?.broughtForward || 0) + totalDebit - totalCredit)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </StyledTableContainer>}
        </BgBox>
    )
}

export default TrkOpLedgerPage;
